import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Sprint 18 Block F T18.27 — Custel DS OTPInput.
 *
 * 6-box auto-tab numeric input. Tek bir kutuya rakam girince sonraki kutuya geçer;
 * backspace ile öncekine. Paste support (clipboard'tan 6 haneli rakamı bulup dağıtır).
 * Mobil klavyede `inputmode="numeric"` + `autoComplete="one-time-code"` (iOS SMS autofill).
 *
 * Props kontrollu (`value` + `onChange`). Tamamlanınca `onComplete` çağrılır.
 */
export interface CustelOTPInputProps {
  /** 0-6 karakter, sadece rakam. */
  value: string;
  onChange: (value: string) => void;
  /** 6 hane dolduğunda tetiklenir (otomatik submit için). */
  onComplete?: (value: string) => void;
  /** Hata stateı (kırmızı border). */
  error?: boolean;
  /** Disabled state (verify sırasında loading'de). */
  disabled?: boolean;
  /** A11y label. */
  ariaLabel?: string;
  /** Auto focus ilk kutuya (sayfa açıldığında). */
  autoFocus?: boolean;
}

const LENGTH = 6;

export function CustelOTPInput({
  value,
  onChange,
  onComplete,
  error = false,
  disabled = false,
  ariaLabel,
  autoFocus = false,
}: CustelOTPInputProps) {
  const { t } = useTranslation('components');
  const groupLabel = ariaLabel ?? t('otp.label');
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const [focusIdx, setFocusIdx] = useState<number | null>(null);

  // Pad value to 6 chars for rendering
  const digits = Array.from({ length: LENGTH }, (_, i) => value[i] ?? '');

  useEffect(() => {
    if (autoFocus) {
      inputsRef.current[0]?.focus();
    }
  }, [autoFocus]);

  function setDigit(idx: number, digit: string) {
    // sadece tek karakter rakam
    if (digit && !/^\d$/.test(digit)) return;
    const next = digits.slice();
    next[idx] = digit;
    const newValue = next.join('').replace(/\s/g, '').slice(0, LENGTH);
    onChange(newValue);

    if (digit && idx < LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
    if (newValue.length === LENGTH) {
      onComplete?.(newValue);
    }
  }

  function handleKeyDown(idx: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      if (!digits[idx] && idx > 0) {
        inputsRef.current[idx - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, LENGTH);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted);
    const lastIdx = Math.min(pasted.length, LENGTH) - 1;
    inputsRef.current[lastIdx]?.focus();
    if (pasted.length === LENGTH) {
      onComplete?.(pasted);
    }
  }

  return (
    <div className="flex items-center gap-2" role="group" aria-label={groupLabel}>
      {digits.map((digit, idx) => (
        <input
          key={idx}
          ref={(el) => {
            inputsRef.current[idx] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={idx === 0 ? 'one-time-code' : 'off'}
          aria-label={t('otp.digit', { label: groupLabel, position: idx + 1 })}
          maxLength={1}
          disabled={disabled}
          value={digit}
          onChange={(e) => setDigit(idx, e.target.value.slice(-1))}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          onFocus={() => setFocusIdx(idx)}
          onBlur={() => setFocusIdx(null)}
          className={`h-12 w-10 rounded border text-center font-mono text-xl focus:outline-none focus:ring-2 ${
            error
              ? 'border-custel-danger focus:ring-custel-danger'
              : focusIdx === idx
                ? 'border-custel-text focus:ring-custel-text'
                : 'border-custel-border focus:ring-custel-border-strong'
          } ${disabled ? 'cursor-not-allowed bg-custel-bg-subtle text-custel-text-tertiary' : 'bg-custel-surface text-custel-text'}`}
        />
      ))}
    </div>
  );
}
